import { unionBy, differenceBy } from 'mojito/utils';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { resolveSelectionGroupsByName } from 'modules/market/helper/market-helper';

const { SortingHelper } = MojitoPresentation.Utils;
const EventUtils = MojitoServices.SportsContent.Events.utils;

const isNoGoalScorer = selection => selection.type === 'X' || selection.type === undefined;

// The no goalscorer selection could have type 'X' or undefined.
// We will check them and place this kind of selections in the end of the list.
const compareByNoGoalScorer = (s1, s2) => {
    if (isNoGoalScorer(s1) && isNoGoalScorer(s2)) {
        return 0;
    }
    if (isNoGoalScorer(s1)) {
        return 1;
    }
    if (isNoGoalScorer(s2)) {
        return -1;
    }
};

/**
 * Utility functions associated with player incident market content module.
 *
 * @class PlayerIncidentUtils
 * @memberof Mojito.Modules.AggregatedMarketPane
 */
export default class PlayerIncidentUtils {
    /**
     * Resolves unique players list from conjunction of players from each market.
     * Sorts first market selections by price and name.
     * All other markets will be sorted according to players order of the first market.
     * That way selections for the particular player will appear on the same line.
     * This function gives a mask that can be used for sorting selections in groups and
     * to display selection titles next to each row in player incident or goalscorer market.
     * E.g., if 1st goalscorer sort is : ['John', 'Otis', 'James'], the same sorting
     * players order will be applied for selections from other goalscorer markets.
     *
     * @param {string} language - Language code.
     * @param {Array<object>} markets - Markets list.
     * @returns {Array<string>} List of player names that represents mask.
     * @function Mojito.Modules.AggregatedMarketPane.PlayerIncidentUtils.getPlayersMask
     */
    static getPlayersMask(language, markets) {
        const firstMarket = markets[0];
        const selections = firstMarket ? Object.values(firstMarket.selectionMap) : [];
        const playersFirstMarket = selections
            .toSorted(PlayerIncidentUtils.getSelectionsComparator(language))
            .map(PlayerIncidentUtils.createPlayer);
        const allPlayers = PlayerIncidentUtils.resolvePlayers(markets);
        const missingPlayers = differenceBy(allPlayers, playersFirstMarket, 'name').sort(
            (player1, player2) => SortingHelper.compare(player1.name, player2.name, language)
        );
        return [...playersFirstMarket, ...missingPlayers];
    }

    /**
     * Gives sporting comparator function to sort selections by price and name. "No goal scorer" selections
     * will be sorted out to the end of the list.
     *
     * @param {string} language - Language code.
     * @returns {Function} Comparator function to be used in sorting.
     * @function Mojito.Modules.AggregatedMarketPane.PlayerIncidentUtils.getSelectionsComparator
     */
    static getSelectionsComparator(language) {
        return SortingHelper.composeComparators(
            compareByNoGoalScorer,
            EventUtils.compareByPrice,
            (s1, s2) => SortingHelper.compare(s1.name, s2.name, language)
        );
    }

    /**
     * Gives list of selections from particular market organized and sorted by provided player names.
     * If there is no selection for a particular player, then bogus selection will be generated to keep integrity.
     *
     * @param {string} market - Market.
     * @param {string} playerNames - Player names list.
     * @returns {Array<object>} List of selections from a market.
     * @function Mojito.Modules.AggregatedMarketPane.PlayerIncidentUtils.resolveMarketSelectionsLine
     */
    static resolveMarketSelectionsLine(market, playerNames) {
        const compareGroups = PlayerIncidentUtils.selectionGroupsComparator(playerNames);
        // playerNames act as selection types that we will use to group selections.
        // Even though there will be only one selection in each group for player incident and goalscorer markets - we still use helper function
        // here because it knows how to put selections in correct place and how to generate bogus selections to keep line integrity.
        return resolveSelectionGroupsByName(market, playerNames, false).sort(compareGroups).flat();
    }

    /**
     * Creates player object from selection.
     *
     * @param {object} selection - Selection.
     * @returns {{id: string, name: string, selection: object}} Player descriptor object.
     * @function Mojito.Modules.AggregatedMarketPane.PlayerIncidentUtils.createPlayer
     */
    static createPlayer(selection) {
        return { id: selection.id, name: selection.name, selection };
    }

    /**
     * Take players union from all markets excluding duplicates. Contain selection id and name, name is criterion to check for duplicates.
     *
     * @param {Array<object>} markets - Markets list.
     * @returns {Array<object>} List of player objects. Each object generated by {@link Mojito.Modules.AggregatedMarketPane.PlayerIncidentUtils.createPlayer|createPlayer}.
     * @function Mojito.Modules.AggregatedMarketPane.PlayerIncidentUtils.resolvePlayers
     */
    static resolvePlayers(markets) {
        const byName = obj => obj.name;
        const selectionChunks = markets.map(market => market.selections);
        return unionBy(...selectionChunks, byName).map(PlayerIncidentUtils.createPlayer);
    }

    /**
     * Gives sorting comparator function to sort selection groups according to provided sorting mask.
     *
     * @param {Array<string>} sortingMask - List of string values. Typically ordered players list.
     * @returns {Function} Comparator function to be used in sorting.
     * @function Mojito.Modules.AggregatedMarketPane.PlayerIncidentUtils.selectionGroupsComparator
     */
    static selectionGroupsComparator(sortingMask) {
        return (g1, g2) => {
            const [selection1, selection2] = [g1[0], g2[0]];
            const fistIndex = sortingMask.indexOf(selection1.name);
            const secondIndex = sortingMask.indexOf(selection2.name);
            if (fistIndex === -1 && secondIndex === -1) {
                return 0;
            }
            if (fistIndex === -1) {
                return 1;
            }
            if (secondIndex === -1) {
                return -1;
            }
            return fistIndex - secondIndex;
        };
    }
}
